import { Injectable, inject } from '@angular/core';
import { Permission } from './permission.interface';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ResourceTypes } from '../../enums/resource-types.enum';
import { UserCompanyPermissions } from '../../enums/user-company-permissions.enum';
import { Util } from '../../utils/util';

export interface PermissionFormModel {
  meta: FormControl<Permission>;
  granted: FormControl<boolean>;
}

@Injectable()
export class PermissionsFormModel {
  private formBuilder = inject(FormBuilder);

  public form!: FormGroup<{
    company: FormArray<FormGroup<PermissionFormModel>>;
    project: FormArray<FormGroup<PermissionFormModel>>;
    assignment: FormArray<FormGroup<PermissionFormModel>>;
    employee: FormArray<FormGroup<PermissionFormModel>>;
    contact: FormArray<FormGroup<PermissionFormModel>>;
    organization: FormArray<FormGroup<PermissionFormModel>>;
  }>;

  public init(grantedPermissions: Permission[]): void {
    const createPermissionGroup = (
      p: Permission,
      granted: boolean,
    ): FormGroup<PermissionFormModel> =>
      this.formBuilder.group({
        meta: new FormControl<Permission>(
          { value: p, disabled: true },
          { nonNullable: true },
        ),
        granted: new FormControl<boolean>(granted, {
          nonNullable: true,
        }),
      });

    const buildArray = (
      resourceType: ResourceTypes,
      permissionCodes: UserCompanyPermissions[],
    ) =>
      this.formBuilder.array(
        permissionCodes.map((code) => {
          const permissionCode = code as UserCompanyPermissions;
          const permission = grantedPermissions.find(
            (gp) =>
              gp.resourceType === resourceType &&
              gp.permissionCode === permissionCode,
          );

          return createPermissionGroup(
            {
              id: permission?.id || Util.generateGUID(),
              resourceType: resourceType,
              permissionCode: permissionCode,
              resourceId: permission?.resourceId,
            },
            !!permission,
          );
        }),
      );

    const allPermissionCodes = Object.values(UserCompanyPermissions).filter(
      (v) => typeof v === 'number',
    );

    this.form = this.formBuilder.group({
      company: buildArray(ResourceTypes.Company, [
        UserCompanyPermissions.Edit,
        UserCompanyPermissions.Delete,
      ]),
      project: buildArray(ResourceTypes.Project, allPermissionCodes),
      assignment: buildArray(ResourceTypes.Assignment, allPermissionCodes),
      employee: buildArray(ResourceTypes.Employee, [
        UserCompanyPermissions.View,
        UserCompanyPermissions.Edit,
      ]),
      contact: buildArray(ResourceTypes.Contact, allPermissionCodes),
      organization: buildArray(ResourceTypes.Organization, allPermissionCodes),
    });
  }
}
